import { useEffect } from 'react'
import { Textarea, Checkbox, Label, showPromptDialog } from '@bpinternal/ui-kit-next'
import { CancellationReason } from '@bpinternal/const'

const CANCELLATION_REASONS: Record<CancellationReason, string> = {
  too_expensive: 'Too expensive for my current needs',
  not_using_enough: 'Not using Botpress enough',
  missing_feature: 'Missing a key feature I need',
  switched_tool: 'Switched to a different tool/platform',
  technical_issues: 'Experienced technical issues or bugs',
  support_expectations: 'Support or guidance did not meet my expectations',
  temporary_need: 'Only needed Botpress temporarily (testing, short-term project, or pausing)',
} as const

type CancelSubscriptionReasonDialogProps = {
  value?: { reasons: CancellationReason[]; comment: string }
  setValue: (value: { reasons: CancellationReason[]; comment: string }) => void
  setValid: (valid: boolean) => void
  close: () => void
}

export const CancelSubscriptionReasonDialog = ({ value, setValue, setValid }: CancelSubscriptionReasonDialogProps) => {
  useEffect(() => {
    setValid(true)
    setValue({ reasons: [], comment: '' })
  }, [setValid, setValue])

  const handleReasonToggle = (reason: CancellationReason) => {
    if (!value) {
      setValue({ reasons: [reason], comment: '' })
      return
    }

    const newReasons = value.reasons.includes(reason)
      ? value.reasons.filter((id) => id !== reason)
      : [...value.reasons, reason]

    setValue({ reasons: newReasons, comment: value?.comment || '' })
  }

  const handleCommentChange = (newComment: string) => {
    setValue({ reasons: value?.reasons || [], comment: newComment })
  }

  return (
    <div className="w-full max-w-2xl space-y-4">
      <p className="mt-1 text-sm text-muted-foreground">
        Your feedback helps us improve Botpress for everyone. Please select all that apply.
      </p>
      <div className="space-y-3">
        {Object.entries(CANCELLATION_REASONS).map(([id, label]) => (
          <div key={id} className=" flex items-center space-x-3">
            <Checkbox
              id={id}
              checked={value?.reasons.includes(id as CancellationReason)}
              onCheckedChange={() => handleReasonToggle(id as CancellationReason)}
            />
            <Label htmlFor={id} className="text-sm font-medium text-foreground">
              {label}
            </Label>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <Label htmlFor="additional-comment" className="text-sm font-medium">
          Additional comments (optional)
        </Label>
        <Textarea
          id="additional-comment"
          placeholder="Please share any additional feedback..."
          value={value?.comment || ''}
          onChange={(e) => handleCommentChange(e.target.value)}
          className="min-h-[80px]"
        />
      </div>
    </div>
  )
}

export const showCancelSubscriptionReasonDialog = () => {
  return showPromptDialog<{ reasons: CancellationReason[]; comment: string }>(CancelSubscriptionReasonDialog, {
    title: 'Before you go, could you tell us why?',
    confirmLabel: 'Cancel subscription',
    cancelLabel: 'Keep my subscription',
    size: 'max-w-2xl',
  })
}
